// SPDX-License-Identifier: AGPL-3.0-or-later

import * as VoiceStateCommands from '@app/features/devtools/commands/VoiceStateCommands';
import type {VoiceState} from '@app/features/gateway/types/GatewayVoiceTypes';
import * as GuildMemberCommands from '@app/features/member/commands/GuildMemberCommands';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {CheckboxItem} from '@app/features/ui/action_menu/ContextMenu';
import {
	BulkTurnOffCameraIcon,
	CopyIdIcon,
	DisconnectIcon,
	FocusIcon,
	GuildDeafenIcon,
	GuildMuteIcon,
	LocalDisableVideoIcon,
	LocalMuteIcon,
	SelfDeafenIcon,
	SelfMuteIcon,
	SettingsIcon,
	TurnOffCameraIcon,
	TurnOffStreamIcon,
	UnfocusIcon,
} from '@app/features/ui/action_menu/ContextMenuIcons';
import styles from '@app/features/ui/action_menu/items/MenuItems.module.css';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {MenuItemSlider} from '@app/features/ui/action_menu/MenuItemSlider';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {UserSettingsModal} from '@app/features/user/components/modals/UserSettingsModal';
import Users from '@app/features/user/state/Users';
import * as VoiceCallLayoutCommands from '@app/features/voice/commands/VoiceCallLayoutCommands';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import CallMediaPrefs from '@app/features/voice/state/CallMediaPrefs';
import ParticipantVolume from '@app/features/voice/state/ParticipantVolume';
import VoiceCallLayout from '@app/features/voice/state/VoiceCallLayout';
import {useLingui} from '@lingui/react/macro';
import {Track} from 'livekit-client';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useMemo} from 'react';

const logger = new Logger('VoiceParticipantMenuItems');
interface SelfMuteMenuItemProps {
	onClose: () => void;
	connectionId?: string;
	isDeviceSpecific?: boolean;
	label?: string;
}
export const SelfMuteMenuItem: React.FC<SelfMuteMenuItemProps> = observer(
	({connectionId, isDeviceSpecific = false, label}) => {
		const {t} = useLingui();
		const voiceState = connectionId
			? MediaEngine.getVoiceStateByConnectionId(connectionId)
			: MediaEngine.getCurrentUserVoiceState();
		const isSelfMuted = voiceState?.self_mute ?? false;
		const handleToggle = useCallback(() => {
			if (isDeviceSpecific && connectionId) {
				VoiceStateCommands.toggleSelfMuteForConnection(connectionId);
			} else {
				VoiceStateCommands.toggleSelfMute(null);
			}
		}, [connectionId, isDeviceSpecific]);
		return (
			<CheckboxItem
				icon={<SelfMuteIcon className={styles.icon} />}
				checked={isSelfMuted}
				onCheckedChange={handleToggle}
			>
				{label ?? t`Mute`}
			</CheckboxItem>
		);
	},
);
interface SelfDeafenMenuItemProps {
	onClose: () => void;
	connectionId?: string;
	isDeviceSpecific?: boolean;
	label?: string;
}
export const SelfDeafenMenuItem: React.FC<SelfDeafenMenuItemProps> = observer(
	({connectionId, isDeviceSpecific = false, label}) => {
		const {t} = useLingui();
		const voiceState = connectionId
			? MediaEngine.getVoiceStateByConnectionId(connectionId)
			: MediaEngine.getCurrentUserVoiceState();
		const isSelfDeafened = voiceState?.self_deaf ?? false;
		const handleToggle = useCallback(() => {
			if (isDeviceSpecific && connectionId) {
				VoiceStateCommands.toggleSelfDeafenForConnection(connectionId);
			} else {
				VoiceStateCommands.toggleSelfDeaf(null);
			}
		}, [connectionId, isDeviceSpecific]);
		return (
			<CheckboxItem
				icon={<SelfDeafenIcon className={styles.icon} />}
				checked={isSelfDeafened}
				onCheckedChange={handleToggle}
			>
				{label ?? t`Deafen`}
			</CheckboxItem>
		);
	},
);
interface VoiceVideoSettingsMenuItemProps {
	onClose: () => void;
}
export const VoiceVideoSettingsMenuItem: React.FC<VoiceVideoSettingsMenuItemProps> = observer(({onClose}) => {
	const {t} = useLingui();
	const handleClick = useCallback(() => {
		onClose();
		ModalCommands.push(modal(() => <UserSettingsModal initialTab="voice_video" />));
	}, [onClose]);
	return (
		<MenuItem icon={<SettingsIcon className={styles.icon} />} onClick={handleClick}>
			{t`Voice & Video Settings`}
		</MenuItem>
	);
});
interface SelfTurnOffCameraMenuItemProps {
	onClose: () => void;
}
export const SelfTurnOffCameraMenuItem: React.FC<SelfTurnOffCameraMenuItemProps> = observer(({onClose}) => {
	const {t} = useLingui();
	const connectionId = MediaEngine.connectionId;
	const voiceState = connectionId ? MediaEngine.getVoiceStateByConnectionId(connectionId) : null;
	const isCameraOn = voiceState?.self_video ?? false;
	const handleClick = useCallback(() => {
		if (connectionId) VoiceStateCommands.turnOffCameraForConnection(connectionId);
		onClose();
	}, [connectionId, onClose]);
	if (!isCameraOn) return null;
	return (
		<MenuItem icon={<TurnOffCameraIcon className={styles.icon} />} onClick={handleClick}>
			{t`Turn Off Camera`}
		</MenuItem>
	);
});
interface SelfTurnOffStreamMenuItemProps {
	onClose: () => void;
}
export const SelfTurnOffStreamMenuItem: React.FC<SelfTurnOffStreamMenuItemProps> = observer(({onClose}) => {
	const {t} = useLingui();
	const connectionId = MediaEngine.connectionId;
	const voiceState = connectionId ? MediaEngine.getVoiceStateByConnectionId(connectionId) : null;
	const isStreaming = voiceState?.self_stream ?? false;
	const handleClick = useCallback(() => {
		if (connectionId) VoiceStateCommands.turnOffStreamForConnection(connectionId);
		onClose();
	}, [connectionId, onClose]);
	if (!isStreaming) return null;
	return (
		<MenuItem icon={<TurnOffStreamIcon className={styles.icon} />} onClick={handleClick}>
			{t`Turn Off Stream`}
		</MenuItem>
	);
});
interface ParticipantVolumeSliderProps {
	userId: string;
}
export const ParticipantVolumeSlider: React.FC<ParticipantVolumeSliderProps> = observer(({userId}) => {
	const {t} = useLingui();
	const participantVolume = ParticipantVolume.getVolume(userId);
	const handleChange = useCallback(
		(value: number) => {
			ParticipantVolume.setVolume(userId, value);
			MediaEngine.applyLocalAudioPreferencesForUser(userId);
		},
		[userId],
	);
	return (
		<MenuItemSlider
			label={t`User Volume`}
			value={participantVolume}
			minValue={0}
			maxValue={200}
			onChange={handleChange}
			onFormat={(value) => `${Math.round(value)}%`}
		/>
	);
});
interface ParticipantVolumeSubmenuProps {
	userId: string;
	connectionId?: string | null;
}
export const ParticipantVolumeSubmenu: React.FC<ParticipantVolumeSubmenuProps> = observer(({userId, connectionId}) => {
	const {t} = useLingui();
	const participantVolume = ParticipantVolume.getVolume(userId);
	const connectionVolume = connectionId ? ParticipantVolume.getConnectionVolume(connectionId) : 100;
	const handleUserVolumeChange = useCallback(
		(value: number) => {
			ParticipantVolume.setVolume(userId, value);
			MediaEngine.applyLocalAudioPreferencesForUser(userId);
		},
		[userId],
	);
	const handleConnectionVolumeChange = useCallback(
		(value: number) => {
			if (!connectionId) return;
			ParticipantVolume.setConnectionVolume(connectionId, value);
			MediaEngine.applyLocalAudioPreferencesForUser(userId);
		},
		[connectionId, userId],
	);
	return (
		<MenuItemSubmenu
			label={t`Volume`}
			render={() => (
				<>
					<MenuItemSlider
						label={t`User Volume`}
						value={participantVolume}
						minValue={0}
						maxValue={200}
						onChange={handleUserVolumeChange}
						onFormat={(value) => `${Math.round(value)}%`}
					/>
					{connectionId && (
						<MenuItemSlider
							label={t`Connection Volume`}
							value={connectionVolume}
							minValue={0}
							maxValue={200}
							onChange={handleConnectionVolumeChange}
							onFormat={(value) => `${Math.round(value)}%`}
						/>
					)}
				</>
			)}
		/>
	);
});
interface LocalMuteParticipantMenuItemProps {
	userId: string;
	onClose: () => void;
}
export const LocalMuteParticipantMenuItem: React.FC<LocalMuteParticipantMenuItemProps> = observer(({userId}) => {
	const {t} = useLingui();
	const isLocalMuted = ParticipantVolume.isLocalMuted(userId);
	const handleToggle = useCallback(
		(checked: boolean) => {
			ParticipantVolume.setLocalMute(userId, checked);
			MediaEngine.applyLocalAudioPreferencesForUser(userId);
		},
		[userId],
	);
	return (
		<CheckboxItem
			icon={<LocalMuteIcon className={styles.icon} />}
			checked={isLocalMuted}
			onCheckedChange={handleToggle}
		>
			{t`Mute`}
		</CheckboxItem>
	);
});
interface LocalDisableVideoMenuItemProps {
	userId: string;
	connectionId: string;
}
export const LocalDisableVideoMenuItem: React.FC<LocalDisableVideoMenuItemProps> = observer(
	({userId, connectionId}) => {
		const {t} = useLingui();
		const callId = MediaEngine.connectionId ?? '';
		const identity = `user_${userId}_${connectionId}`;
		const disabled = callId ? CallMediaPrefs.isVideoDisabled(callId, identity) : false;
		const handleToggle = useCallback(
			(checked: boolean) => {
				const id = MediaEngine.connectionId ?? '';
				if (!id) {
					const error = new Error('Unable to toggle local video without an active connection');
					logger.error('Local disable video toggle invoked without connection id', {
						userId,
						connectionId,
						identity,
					});
					throw error;
				}
				MediaEngine.setLocalVideoDisabled(identity, checked);
			},
			[identity, connectionId, userId],
		);
		return (
			<CheckboxItem
				icon={<LocalDisableVideoIcon className={styles.icon} />}
				checked={disabled}
				onCheckedChange={handleToggle}
			>
				{t`Disable Video (Local)`}
			</CheckboxItem>
		);
	},
);
interface GuildMuteMenuItemProps {
	userId: string;
	guildId: string;
	onClose: () => void;
}
export const GuildMuteMenuItem: React.FC<GuildMuteMenuItemProps> = observer(function GuildMuteMenuItem({
	userId,
	guildId,
}) {
	const {t} = useLingui();
	const member = GuildMembers.getMember(guildId, userId);
	const isGuildMuted = member?.mute ?? false;
	const isTimedOut = member?.isTimedOut() ?? false;
	const handleToggle = useCallback(
		async (checked: boolean) => {
			try {
				await GuildMemberCommands.update(guildId, userId, {mute: checked});
				if (checked) SoundCommands.playSound(SoundType.Mute);
				else SoundCommands.playSound(SoundType.Unmute);
			} catch {}
		},
		[guildId, userId],
	);
	return (
		<CheckboxItem
			icon={<GuildMuteIcon className={styles.icon} />}
			checked={!!isGuildMuted}
			onCheckedChange={handleToggle}
			danger
			disabled={isTimedOut}
		>
			{t`Community Mute`}
		</CheckboxItem>
	);
});
interface GuildDeafenMenuItemProps {
	userId: string;
	guildId: string;
	onClose: () => void;
}
export const GuildDeafenMenuItem: React.FC<GuildDeafenMenuItemProps> = observer(function GuildDeafenMenuItem({
	userId,
	guildId,
}) {
	const {t} = useLingui();
	const member = GuildMembers.getMember(guildId, userId);
	const isGuildDeafened = member?.deaf ?? false;
	const handleToggle = useCallback(
		async (checked: boolean) => {
			try {
				await GuildMemberCommands.update(guildId, userId, {deaf: checked});
				if (checked) SoundCommands.playSound(SoundType.Deaf);
				else SoundCommands.playSound(SoundType.Undeaf);
			} catch {}
		},
		[guildId, userId],
	);
	return (
		<CheckboxItem
			icon={<GuildDeafenIcon className={styles.icon} />}
			checked={!!isGuildDeafened}
			onCheckedChange={handleToggle}
			danger
		>
			{t`Community Deafen`}
		</CheckboxItem>
	);
});
interface DisconnectParticipantMenuItemProps {
	userId: string;
	guildId: string;
	participantName: string;
	connectionId?: string;
	onClose: () => void;
	label?: string;
}
export const DisconnectParticipantMenuItem: React.FC<DisconnectParticipantMenuItemProps> = observer(
	function DisconnectParticipantMenuItem({userId, guildId, connectionId, onClose, label}) {
		const {t} = useLingui();
		const currentUser = Users.currentUser;
		const isSelf = currentUser?.id === userId;
		const handleClick = useCallback(async () => {
			onClose();
			if (isSelf) {
				const isCurrentDevice = !connectionId || connectionId === MediaEngine.connectionId;
				if (isCurrentDevice) {
					await MediaEngine.disconnectFromVoiceChannel('user');
				} else {
					const cid = connectionId ?? MediaEngine.connectionId ?? null;
					if (cid) {
						MediaEngine.disconnectRemoteDevice(guildId, cid);
					}
				}
			} else {
				try {
					await GuildMemberCommands.update(guildId, userId, {channel_id: null, connection_id: connectionId});
				} catch {}
			}
		}, [guildId, userId, connectionId, onClose, isSelf]);
		const defaultLabel = connectionId ? t`Disconnect Device` : t`Disconnect`;
		return (
			<MenuItem icon={<DisconnectIcon className={styles.icon} />} onClick={handleClick} danger>
				{label ?? defaultLabel}
			</MenuItem>
		);
	},
);
interface TurnOffDeviceCameraMenuItemProps {
	onClose: () => void;
	connectionId: string;
}
export const TurnOffDeviceCameraMenuItem: React.FC<TurnOffDeviceCameraMenuItemProps> = observer(
	({connectionId, onClose}) => {
		const {t} = useLingui();
		const voiceState = MediaEngine.getVoiceStateByConnectionId(connectionId);
		const isCameraOn = voiceState?.self_video ?? false;
		const handleClick = useCallback(() => {
			VoiceStateCommands.turnOffCameraForConnection(connectionId);
			onClose();
		}, [connectionId, onClose]);
		if (!isCameraOn) return null;
		return (
			<MenuItem icon={<TurnOffCameraIcon className={styles.icon} />} onClick={handleClick}>
				{t`Turn Off Device Camera`}
			</MenuItem>
		);
	},
);
interface TurnOffDeviceStreamMenuItemProps {
	onClose: () => void;
	connectionId: string;
}
export const TurnOffDeviceStreamMenuItem: React.FC<TurnOffDeviceStreamMenuItemProps> = observer(
	({connectionId, onClose}) => {
		const {t} = useLingui();
		const voiceState = MediaEngine.getVoiceStateByConnectionId(connectionId);
		const isStreaming = voiceState?.self_stream ?? false;
		const handleClick = useCallback(() => {
			VoiceStateCommands.turnOffStreamForConnection(connectionId);
			onClose();
		}, [connectionId, onClose]);
		if (!isStreaming) return null;
		return (
			<MenuItem icon={<TurnOffStreamIcon className={styles.icon} />} onClick={handleClick}>
				{t`Turn Off Device Stream`}
			</MenuItem>
		);
	},
);
interface CopyDeviceIdMenuItemProps {
	onClose: () => void;
	connectionId: string;
}
export const CopyDeviceIdMenuItem: React.FC<CopyDeviceIdMenuItemProps> = observer(({connectionId, onClose}) => {
	const {t, i18n} = useLingui();
	const handleClick = useCallback(() => {
		TextCopyCommands.copy(i18n, connectionId, true).catch(() => {});
		onClose();
	}, [connectionId, onClose, i18n]);
	return (
		<MenuItem icon={<CopyIdIcon className={styles.icon} />} onClick={handleClick}>
			{t`Copy Device ID`}
		</MenuItem>
	);
});
interface BulkMuteDevicesMenuItemProps {
	userVoiceStates: Array<{connectionId: string; voiceState: VoiceState}>;
	onClose: () => void;
}
export const BulkMuteDevicesMenuItem: React.FC<BulkMuteDevicesMenuItemProps> = observer(
	({userVoiceStates, onClose}) => {
		const {t} = useLingui();
		const allMuted = useMemo(() => userVoiceStates.every(({voiceState}) => voiceState.self_mute), [userVoiceStates]);
		const handleClick = useCallback(() => {
			const connectionIds = userVoiceStates.map(({connectionId}) => connectionId);
			const targetMute = !allMuted;
			VoiceStateCommands.bulkMuteConnections(connectionIds, targetMute);
			if (targetMute) SoundCommands.playSound(SoundType.Mute);
			else SoundCommands.playSound(SoundType.Unmute);
			onClose();
		}, [userVoiceStates, allMuted, onClose]);
		return (
			<MenuItem icon={<GuildMuteIcon className={styles.icon} />} onClick={handleClick}>
				{allMuted ? t`Unmute All Devices` : t`Mute All Devices`}
			</MenuItem>
		);
	},
);
interface BulkDeafenDevicesMenuItemProps {
	userVoiceStates: Array<{connectionId: string; voiceState: VoiceState}>;
	onClose: () => void;
}
export const BulkDeafenDevicesMenuItem: React.FC<BulkDeafenDevicesMenuItemProps> = observer(
	({userVoiceStates, onClose}) => {
		const {t} = useLingui();
		const allDeafened = useMemo(() => userVoiceStates.every(({voiceState}) => voiceState.self_deaf), [userVoiceStates]);
		const handleClick = useCallback(() => {
			const connectionIds = userVoiceStates.map(({connectionId}) => connectionId);
			const targetDeafen = !allDeafened;
			VoiceStateCommands.bulkDeafenConnections(connectionIds, targetDeafen);
			if (targetDeafen) SoundCommands.playSound(SoundType.Deaf);
			else SoundCommands.playSound(SoundType.Undeaf);
			onClose();
		}, [userVoiceStates, allDeafened, onClose]);
		return (
			<MenuItem icon={<GuildDeafenIcon className={styles.icon} />} onClick={handleClick}>
				{allDeafened ? t`Undeafen All Devices` : t`Deafen All Devices`}
			</MenuItem>
		);
	},
);
interface BulkCameraDevicesMenuItemProps {
	userVoiceStates: Array<{connectionId: string; voiceState: VoiceState}>;
	onClose: () => void;
}
export const BulkCameraDevicesMenuItem: React.FC<BulkCameraDevicesMenuItemProps> = observer(
	({userVoiceStates, onClose}) => {
		const {t} = useLingui();
		const handleClick = useCallback(() => {
			const connectionIds = userVoiceStates.map(({connectionId}) => connectionId);
			VoiceStateCommands.bulkTurnOffCameras(connectionIds);
			onClose();
		}, [userVoiceStates, onClose]);
		return (
			<MenuItem icon={<BulkTurnOffCameraIcon className={styles.icon} />} onClick={handleClick}>
				{t`Turn Off All Device Cameras`}
			</MenuItem>
		);
	},
);
interface BulkDisconnectDevicesMenuItemProps {
	userVoiceStates: Array<{connectionId: string; voiceState: VoiceState}>;
	onClose: () => void;
}
export const BulkDisconnectDevicesMenuItem: React.FC<BulkDisconnectDevicesMenuItemProps> = observer(
	({userVoiceStates, onClose}) => {
		const {t} = useLingui();
		const handleClick = useCallback(async () => {
			await VoiceStateCommands.bulkDisconnect(userVoiceStates.map(({connectionId}) => connectionId));
			onClose();
		}, [userVoiceStates, onClose]);
		return (
			<MenuItem icon={<DisconnectIcon className={styles.icon} />} onClick={handleClick} danger>
				{t`Disconnect All Devices`}
			</MenuItem>
		);
	},
);
interface FocusParticipantMenuItemProps {
	userId: string;
	connectionId: string;
	isScreenShare?: boolean;
	onClose: () => void;
}
export const FocusParticipantMenuItem: React.FC<FocusParticipantMenuItemProps> = observer(
	({userId, connectionId, isScreenShare = false, onClose}) => {
		const {t} = useLingui();
		const identity = `user_${userId}_${connectionId}`;
		const pinnedParticipantSource = VoiceCallLayout.pinnedParticipantSource;
		const isFocused =
			VoiceCallLayout.pinnedParticipantIdentity === identity &&
			(pinnedParticipantSource == null ||
				pinnedParticipantSource === (isScreenShare ? Track.Source.ScreenShare : Track.Source.Camera));
		const hasMultipleConnections = useMemo(() => {
			const allStates = MediaEngine.getAllVoiceStates();
			let count = 0;
			Object.values(allStates).forEach((guildData) => {
				Object.values(guildData).forEach((channelData) => {
					Object.values(channelData).forEach((vs: VoiceState) => {
						if (vs.user_id === userId) count++;
					});
				});
			});
			return count > 1;
		}, [userId]);
		const handleClick = useCallback(() => {
			if (isFocused) {
				VoiceCallLayoutCommands.setPinnedParticipant(null);
				VoiceCallLayoutCommands.setLayoutMode('grid');
				VoiceCallLayoutCommands.markUserOverride();
			} else {
				VoiceCallLayoutCommands.setLayoutMode('focus');
				VoiceCallLayoutCommands.setPinnedParticipant(
					identity,
					isScreenShare ? Track.Source.ScreenShare : Track.Source.Camera,
				);
				VoiceCallLayoutCommands.markUserOverride();
			}
			onClose();
		}, [identity, onClose, isFocused, isScreenShare]);
		const focusLabel = (() => {
			if (isFocused) return t`Unfocus`;
			if (hasMultipleConnections) return t`Focus This Device`;
			return t`Focus This Person`;
		})();
		return (
			<MenuItem
				icon={isFocused ? <UnfocusIcon className={styles.icon} /> : <FocusIcon className={styles.icon} />}
				onClick={handleClick}
			>
				{focusLabel}
			</MenuItem>
		);
	},
);
